import { motion, AnimatePresence } from 'framer-motion';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Button } from '@app/app/_components/Button/Button';
import { UserIcon } from '@app/app/_icons/UserIcon';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { useTranslations } from 'next-intl';
import { LoginWidget } from '@app/app/_widgets/Login/LoginWidget';
import { SelectClientWidget } from '@app/app/_widgets/SelectClient/SelectClientWidget';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { Link } from '@app/app/_components/Link/Link';
import { useState } from 'react';
import { useLogin } from '@app/app/auth/login/hooks/useLogin';
import { LoginModalSection } from '@app/app/auth/login/components/LoginModalSection';
import { TenantMetadata } from '@app/infrastructure/tenant/model/TenantMetadata';
import { LanguageSelector } from '@app/app/_widgets/LanguageSelector/LanguageSelector';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    toHomePage: () => void;
    authState: AuthState;
    handleSubmit: () => void;
    onAuthSuccess?: () => void;
    clients: Client[];
    callNumber: string;
    bookConsultationUrl: string;
    tenant: TenantMetadata;
}

export function MobileSideMenu({
    isOpen,
    onClose,
    toHomePage,
    authState,
    handleSubmit,
    onAuthSuccess,
    clients,
    callNumber,
    bookConsultationUrl,
    tenant,
}: Props) {
    const t = useTranslations();

    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isSelectClientOpen, setIsSelectClientOpen] = useState(false);

    const isAuthenticated = authState.authenticated;

    const handleLoginOpen = (shouldOpen: boolean) => {
        setIsLoginOpen(shouldOpen);
    };

    const handleSelectClientOpen = (shouldOpen: boolean) => {
        setIsSelectClientOpen(shouldOpen);
    };

    const login = useLogin(authState, handleSubmit, handleLoginOpen, handleSelectClientOpen, onAuthSuccess);

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            className="fixed inset-y-0 right-0 z-50 w-full bg-white shadow-lg md:hidden"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'tween', duration: 0.3 }}>
                            <div className="flex flex-col justify-between px-6 py-4">
                                <div className="flex flex-col">
                                    <GhostButton onClick={onClose} className="self-end">
                                        <CloseOutlinedCircleIcon className="size-8" />
                                    </GhostButton>
                                    <div className="mb-4 mt-3">
                                        <Paragraph className="mb-4 text-primary-500">{t('general.language')}</Paragraph>
                                        <LanguageSelector />
                                    </div>
                                    <div className="mb-8">
                                        <Paragraph className="mb-4 text-primary-500">
                                            {t('eshop.life.mobileMenu.help')}
                                        </Paragraph>

                                        <div className="space-y-4">
                                            <GhostButton href={`tel:${callNumber}`} className="w-full text-left">
                                                <Heading level="h3" className="font-normal">
                                                    {t('eshop.life.mobileMenu.callToCustomerSupport')}
                                                </Heading>
                                                <Paragraph isGray size="small">
                                                    {t('eshop.life.mobileMenu.callToCustomerSupportDescription')}
                                                </Paragraph>
                                            </GhostButton>

                                            <GhostButton
                                                href={bookConsultationUrl}
                                                target="_blank"
                                                className="w-full text-left">
                                                <Heading level="h3" className="font-normal">
                                                    {t('eshop.life.mobileMenu.bookAConsultation')}
                                                </Heading>
                                                <Paragraph isGray size="small">
                                                    {t('eshop.life.mobileMenu.bookAConsultationDescription')}
                                                </Paragraph>
                                            </GhostButton>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <Paragraph className="mb-4 text-primary-500">
                                            {t('eshop.life.mobileMenu.whatNext')}
                                        </Paragraph>

                                        <div className="space-y-4">
                                            <GhostButton
                                                className="w-full border-b border-neutral-300 pb-4 text-left"
                                                onClick={toHomePage}>
                                                <Heading level="h3" className="font-normal">
                                                    {t('eshop.life.mobileMenu.backToHomepage')}
                                                </Heading>
                                            </GhostButton>
                                        </div>
                                    </div>
                                </div>

                                {!isAuthenticated && (
                                    <div className="mt-auto pt-8">
                                        <Button
                                            variant="outlined"
                                            leftAddon={<UserIcon className="size-5 text-neutral-700" />}
                                            className="flex w-full border border-neutral-300"
                                            onClick={login.handleLoginClick}
                                            preventDefault>
                                            <Paragraph>{t('eshop.life.mobileMenu.logIn')}</Paragraph>
                                        </Button>
                                    </div>
                                )}
                                {isAuthenticated && (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="rounded-full bg-neutral-100 p-2.5">
                                                <Link
                                                    href="/self-service/profile"
                                                    aria-label={t('auth.myProfile')}
                                                    className="flex h-full w-full">
                                                    <UserIcon className="size-5 text-black" />
                                                </Link>
                                            </div>
                                            <Paragraph>{authState.selectedClient?.fullName}</Paragraph>
                                        </div>

                                        <TextButton
                                            rightAddon={<ArrowRightIcon className="size-4" />}
                                            onClick={login.handleLogout}>
                                            {t('auth.logout')}
                                        </TextButton>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <LoginModalSection
                isLoginOpen={isLoginOpen}
                isSelectClientOpen={isSelectClientOpen}
                handleLoginClose={login.handleLoginClose}
                handleSelectClientClose={login.handleSelectClientClose}
                loginWidget={
                    <LoginWidget disableTwoFactorAuth onSuccess={login.handleLoginSuccess} noBankAuth tenant={tenant} />
                }
                selectClientWidget={
                    <SelectClientWidget clients={clients} onSuccess={login.handleSelectClientSuccess} />
                }
            />
        </>
    );
}
